import { useCallback, useEffect, useRef, useState } from "react";
import { ApiError, checkoutPix, fetchDocument, getCheckoutStatus, simulatePayment } from "../lib/api";
import { trackPurchase } from "../lib/gtag";
import type { CheckoutBuyer, CheckoutSession, ReleasedDocument } from "../types/api";

export type CheckoutPhase = "idle" | "creating" | "pending" | "expired" | "confirming" | "success";

const POLL_MS = 3000;
const SUCCESS_MS = 1200;

interface Args {
  protocolo: string;
  buyer: CheckoutBuyer;
  price: number;
  onReleased: (doc: ReleasedDocument) => void;
}

const toError = (e: unknown): ApiError => (e instanceof ApiError ? e : new ApiError("UNKNOWN", "Não foi possível concluir a operação."));

export function usePixCheckout({ protocolo, buyer, price, onReleased }: Args) {
  const [session, setSession] = useState<CheckoutSession | null>(null);
  const [phase, setPhase] = useState<CheckoutPhase>("idle");
  const [error, setError] = useState<ApiError | null>(null);
  const [open, setOpen] = useState(false);
  const [simulating, setSimulating] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  const onReleasedRef = useRef(onReleased);
  const confirming = useRef(false);
  const tracked = useRef<string | null>(null);
  const successTimer = useRef<number | undefined>(undefined);
  onReleasedRef.current = onReleased;

  useEffect(() => () => window.clearTimeout(successTimer.current), []);

  const create = async () => {
    setError(null);
    setPhase("creating");
    try {
      const s = await checkoutPix(protocolo, buyer, price);
      setSession(s);
      setNow(Date.now());
      setPhase("pending");
      setOpen(true);
    } catch (e: unknown) {
      setError(toError(e));
      setPhase(session ? "expired" : "idle");
    }
  };

  const start = () => {
    if (session && phase === "pending") {
      setOpen(true);
      return;
    }
    void create();
  };

  const confirm = useCallback(async (s: CheckoutSession) => {
    if (confirming.current) return;
    confirming.current = true;
    setPhase("confirming");
    try {
      const doc = await fetchDocument(protocolo, s.releaseToken);
      if (s.mode !== "mock" && tracked.current !== protocolo) {
        tracked.current = protocolo;
        trackPurchase(protocolo, s.valor);
      }
      setPhase("success");
      setOpen(true);
      successTimer.current = window.setTimeout(() => {
        setOpen(false);
        onReleasedRef.current(doc);
      }, SUCCESS_MS);
    } catch (e: unknown) {
      const err = toError(e);
      setPhase("pending");
      if (err.code !== "PAYMENT_REQUIRED") setError(err);
      confirming.current = false;
    }
  }, [protocolo]);

  useEffect(() => {
    if (phase !== "pending" || !session) return;
    const expiresAt = new Date(session.expiresAt).getTime();
    const id = window.setInterval(() => {
      const t = Date.now();
      setNow(t);
      if (t >= expiresAt) setPhase("expired");
    }, 1000);
    return () => window.clearInterval(id);
  }, [phase, session]);

  useEffect(() => {
    if (phase !== "pending" || !session) return;
    let cancelled = false;
    let timer: number | undefined;
    const poll = async () => {
      try {
        const res = await getCheckoutStatus(session.paymentId);
        if (cancelled) return;
        if (res.status === "PAID") return void confirm(session);
        if (res.status === "EXPIRED") return setPhase("expired");
      } catch {
        // falha transitória: tenta novamente no próximo ciclo
      }
      if (!cancelled) timer = window.setTimeout(poll, POLL_MS);
    };
    timer = window.setTimeout(poll, POLL_MS);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [phase, session, confirm]);

  const simulate = async () => {
    if (!session) return;
    setSimulating(true);
    setError(null);
    try {
      await simulatePayment(session.paymentId);
      await confirm(session);
    } catch (e: unknown) {
      setError(toError(e));
    } finally {
      setSimulating(false);
    }
  };

  const remainingMs = session ? Math.max(0, new Date(session.expiresAt).getTime() - now) : 0;
  return { session, phase, error, open, setOpen, start, renew: create, simulate, simulating, remainingMs };
}
